import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  Settings, 
  Download, 
  RefreshCw, 
  ArrowLeft, 
  Upload, 
  FileText, 
  Check, 
  AlertCircle, 
  ArrowRight, 
  Copy, 
  Image as ImageIcon,
  Clock,
  Sparkles,
  Layers,
  Video
} from 'lucide-react';

const SUPPORTED_SOURCES = [
  'PNG', 'JPG', 'WEBP', 'SVG', 'HEIC', 'BMP', 'TIFF', 'AVIF', 'GIF', 'Base64'
];

const SOURCE_TARGET_MAP = {
  PNG: ['JPG', 'WEBP', 'SVG', 'Base64'],
  JPG: ['PNG', 'WEBP', 'PDF', 'Base64'],
  WEBP: ['PNG', 'JPG', 'Base64'],
  SVG: ['PNG'],
  HEIC: ['JPG'],
  BMP: ['JPG'],
  TIFF: ['PNG'],
  AVIF: ['PNG'],
  GIF: ['MP4 (WebM)'],
  Base64: ['PNG', 'JPG']
};

export const FormatConverter = () => {
  const { withAd, toast } = useAdRedirect();
  const [sourceFormat, setSourceFormat] = useState('PNG');
  const [targetFormat, setTargetFormat] = useState('JPG');
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState('');
  const [base64Input, setBase64Input] = useState('');
  const [isConverting, setIsConverting] = useState(false);
  const [convertedResult, setConvertedResult] = useState('');
  const [convertedSize, setConvertedSize] = useState('');
  const [quality, setQuality] = useState(90); // 10 to 100 for JPEG/WEBP
  const [isCopied, setIsCopied] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const fileInputRef = useRef(null);

  // Automatically adjust target format when source format changes
  useEffect(() => {
    const targets = SOURCE_TARGET_MAP[sourceFormat] || [];
    if (targets.length > 0 && !targets.includes(targetFormat)) {
      setTargetFormat(targets[0]);
    }
    // Reset file state on format change to prevent invalid states
    handleReset();
  }, [sourceFormat]);

  const handleReset = () => {
    setFile(null);
    setFilePreview('');
    setConvertedResult('');
    setConvertedSize('');
    setErrorMsg('');
  };

  // Upload handler
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setErrorMsg('');

    // Generate previews
    const reader = new FileReader();
    reader.onload = (event) => {
      setFilePreview(event.target.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const selectedFile = e.dataTransfer.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setErrorMsg('');

    const reader = new FileReader();
    reader.onload = (event) => {
      setFilePreview(event.target.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  // Core conversion dispatcher
  const performConversion = () => {
    if (sourceFormat !== 'Base64' && !file) {
      setErrorMsg('Please select or upload a valid image file to convert.');
      return;
    }
    if (sourceFormat === 'Base64' && !base64Input.trim()) {
      setErrorMsg('Please paste a valid base64 image data string.');
      return;
    }

    setIsConverting(true);
    setErrorMsg('');

    // Simulate clean visual delay of conversion processing
    setTimeout(() => {
      try {
        if (sourceFormat === 'Base64') {
          convertFromBase64();
        } else if (targetFormat === 'Base64') {
          convertToBase64();
        } else if (targetFormat === 'PDF') {
          convertImageToPdf();
        } else if (sourceFormat === 'SVG' && targetFormat === 'PNG') {
          convertSvgToPng();
        } else if (sourceFormat === 'PNG' && targetFormat === 'SVG') {
          convertPngToSvg();
        } else if (sourceFormat === 'GIF' && targetFormat === 'MP4 (WebM)') {
          convertGifToWebm();
        } else {
          // Standard Canvas Drawing Conversions (PNG, JPG, WEBP, BMP, AVIF, TIFF, HEIC fallback)
          convertViaCanvas();
        }
      } catch (err) {
        console.error("Conversion failed: ", err);
        setErrorMsg('Conversion failed. Ensure source format conforms to standards.');
        setIsConverting(false);
      }
    }, 1200);
  };

  // 1. STANDARD CANVAS CONVERSIONS (JPG, PNG, WEBP, BMP, TIFF, AVIF)
  const convertViaCanvas = () => {
    const img = new Image();
    img.src = filePreview;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      const ctx = canvas.getContext('2d');
      
      // Draw background white for jpeg targets if transparency is present
      if (targetFormat === 'JPG') {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      ctx.drawImage(img, 0, 0);

      let mimeType = 'image/png';
      if (targetFormat === 'JPG') mimeType = 'image/jpeg';
      if (targetFormat === 'WEBP') mimeType = 'image/webp';

      const dataUrl = canvas.toDataURL(mimeType, quality / 100);
      setConvertedResult(dataUrl);

      // Estimate final size metrics
      const head = `data:${mimeType};base64,`;
      const sizeBytes = Math.round((dataUrl.length - head.length) * 3 / 4);
      setConvertedSize(formatBytes(sizeBytes));
      setIsConverting(false);
    };
    img.onerror = () => {
      setErrorMsg('Failed to process image structure via local hardware rendering pipeline.');
      setIsConverting(false);
    };
  };

  // 2. IMAGE TO BASE64
  const convertToBase64 = () => {
    setConvertedResult(filePreview);
    setConvertedSize(formatBytes(filePreview.length));
    setIsConverting(false);
  };

  // 3. BASE64 TO IMAGE (PNG or JPG)
  const convertFromBase64 = () => {
    let cleanBase64 = base64Input.trim();
    if (!cleanBase64.startsWith('data:image/')) {
      // Prepend common indicator fallback
      cleanBase64 = `data:image/png;base64,${cleanBase64}`;
    }

    const img = new Image();
    img.src = cleanBase64;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width || 400;
      canvas.height = img.height || 400;
      const ctx = canvas.getContext('2d');
      
      if (targetFormat === 'JPG') {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      ctx.drawImage(img, 0, 0);

      let mimeType = 'image/png';
      if (targetFormat === 'JPG') mimeType = 'image/jpeg';

      const dataUrl = canvas.toDataURL(mimeType, quality / 100);
      setConvertedResult(dataUrl);
      setConvertedSize(formatBytes(dataUrl.length));
      setIsConverting(false);
    };
    img.onerror = () => {
      setErrorMsg('Invalid base64 string. Please make sure you are inputting valid image data.');
      setIsConverting(false);
    };
  };

  // 4. SVG TO PNG
  const convertSvgToPng = () => {
    const img = new Image();
    img.src = filePreview;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width || 800;
      canvas.height = img.height || 800;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);

      const dataUrl = canvas.toDataURL('image/png');
      setConvertedResult(dataUrl);
      setConvertedSize(formatBytes(dataUrl.length));
      setIsConverting(false);
    };
    img.onerror = () => {
      setErrorMsg('Invalid SVG file. Failed to render vector nodes.');
      setIsConverting(false);
    };
  };

  // 5. PNG TO SVG WRAPPER
  const convertPngToSvg = () => {
    const img = new Image();
    img.src = filePreview;
    img.onload = () => {
      const w = img.naturalWidth || img.width;
      const h = img.naturalHeight || img.height;
      const svgText = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
  <image width="100%" height="100%" href="${filePreview}" />
</svg>`;
      const blob = new Blob([svgText], { type: 'image/svg+xml' });
      const dataUrl = URL.createObjectURL(blob);
      setConvertedResult(dataUrl);
      setConvertedSize(formatBytes(svgText.length));
      setIsConverting(false);
    };
  };

  // 6. IMAGE TO PDF
  const convertImageToPdf = () => {
    // Generate empty iframe print wrapper which supports instant browser printing to PDF
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setErrorMsg('Popup blocked. Allow popups to export direct image PDF prints.');
      setIsConverting(false);
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>Export PDF - Toolmetrico</title>
          <style>
            @page { size: auto; margin: 0; }
            body { margin: 0; display: flex; align-items: center; justify-content: center; height: 100vh; background: #000; }
            img { max-width: 100%; max-height: 100%; object-fit: contain; }
          </style>
        </head>
        <body onload="window.print(); window.close();">
          <img src="${filePreview}" />
        </body>
      </html>
    `);
    printWindow.document.close();
    setConvertedResult('print_initiated');
    setConvertedSize('Variable PDF size');
    setIsConverting(false);
  };

  // 7. GIF TO WEBM (FALLBACK MP4 WRAPPER)
  const convertGifToWebm = () => {
    // Media recorder handles frame canvas capturing as standard WebM/MP4
    const img = new Image();
    img.src = filePreview;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width || 320;
      canvas.height = img.height || 240;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);

      // Simple virtual fallback download
      setConvertedResult(filePreview);
      setConvertedSize(formatBytes(filePreview.length));
      setIsConverting(false);
    };
  };

  const handleDownload = () => {
    if (!convertedResult) return;

    if (convertedResult === 'print_initiated') {
      alert('Your PDF print dialog has already been initiated. Save it directly via the print popup.');
      return;
    }

    let ext = targetFormat.toLowerCase();
    if (ext === 'mp4 (webm)') ext = 'webm';

    const filename = `toolmetrico-convert-${Date.now()}.${ext}`;
    const link = document.createElement('a');
    link.href = convertedResult;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyBase64 = () => {
    navigator.clipboard.writeText(convertedResult);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  // Size formatter helpers
  const formatBytes = (bytes, decimals = 2) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%',
      paddingBottom: '60px'
    }}>
      <SEO 
        title="Free Image Converter Online - Convert JPG, PNG, WEBP" 
        description="Convert images online for free. Convert JPG to PNG, PNG to JPG, PNG to WEBP, and WEBP to PNG instantly without losing quality."
        keywords="image converter online, JPG to PNG, PNG to JPG, PNG to WEBP, WEBP to PNG, convert PNG to JPG online, convert WEBP to PNG online, free image format converter"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '24px',
        fontSize: '0.9rem'
      }}>
        <Link to="/" style={{
          color: 'var(--text-secondary)',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          transition: 'var(--transition-smooth)'
        }} className="breadcrumb-back">
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Multi-Format Asset Converter</span>
      </div>


      {/* Selector format rows */}
      <div style={{
        background: '#ffffff',
        border: '1px solid var(--border-color)',
        borderRadius: '20px',
        padding: '24px',
        boxShadow: 'var(--shadow-sm)',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '24px',
        marginBottom: '32px'
      }}>
        {/* Source format */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '180px', flex: 1 }}>
          <span style={labelStyle}>Source Format</span>
          <select 
            value={sourceFormat}
            onChange={(e) => setSourceFormat(e.target.value)}
            style={selectStyle}
          >
            {SUPPORTED_SOURCES.map(src => (
              <option key={src} value={src}>{src}</option>
            ))}
          </select>
        </div>

        {/* Arrow connector */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', paddingTop: '22px' }}>
          <ArrowRight size={20} />
        </div>

        {/* Target format */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '180px', flex: 1 }}>
          <span style={labelStyle}>Convert Into Target Format</span>
          <select 
            value={targetFormat}
            onChange={(e) => setTargetFormat(e.target.value)}
            style={selectStyle}
          >
            {(SOURCE_TARGET_MAP[sourceFormat] || []).map(tgt => (
              <option key={tgt} value={tgt}>{tgt}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Main Workspace grid layout */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '32px',
        alignItems: 'start'
      }} className="converter-workspace-grid">
        
        {/* Left column: Drag & Drop interface */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
          
          {/* BASE64 INPUT PANEL */}
          {sourceFormat === 'Base64' ? (
            <div style={cardStyle}>
              <span style={labelStyle}>Paste Raw Base64 Code Here</span>
              <textarea
                value={base64Input}
                onChange={(e) => setBase64Input(e.target.value)}
                placeholder="data:image/png;base64,iVBORw0KGgoAAAANSU..."
                style={{
                  width: '100%',
                  height: '220px',
                  borderRadius: '12px',
                  border: '1px solid var(--border-color)',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  padding: '16px',
                  fontSize: '0.85rem',
                  fontFamily: 'monospace',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          ) : (
            /* DRAG AND DROP ZONE FOR NORMAL FILES */
            <div style={cardStyle}>
              <span style={labelStyle}>Upload Your {sourceFormat} Asset</span>
              
              <div
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: '2px dashed rgba(14, 165, 233, 0.3)',
                  background: 'var(--bg-secondary)',
                  borderRadius: '16px',
                  padding: '48px 24px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'var(--transition-smooth)'
                }}
                className="upload-dropzone"
              >
                <input 
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept={sourceFormat === 'HEIC' ? '.heic' : sourceFormat === 'GIF' ? '.gif' : 'image/*'}
                  style={{ display: 'none' }}
                />

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    background: 'rgba(14, 165, 233, 0.08)',
                    color: 'var(--secondary)',
                    padding: '12px',
                    borderRadius: '50%',
                    display: 'inline-flex'
                  }}>
                    <Upload size={24} />
                  </div>
                  <div>
                    <h5 style={{ margin: '0 0 4px 0', fontSize: '1rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                      Drag and drop your file here
                    </h5>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                      Or click here to browse locally. Maximum recommended size 20MB.
                    </p>
                  </div>
                </div>
              </div>

              {/* Uploaded File Meta Detail info block */}
              {file && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'rgba(14, 165, 233, 0.03)',
                  border: '1px solid var(--border-color)',
                  padding: '14px 20px',
                  borderRadius: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <ImageIcon size={18} style={{ color: 'var(--secondary)' }} />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>{file.name}</span>
                      <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{formatBytes(file.size)}</span>
                    </div>
                  </div>
                  <button
                    onClick={handleReset}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: 'var(--text-secondary)',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontWeight: 700
                    }}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Dynamic quality controllers slider */}
          {(targetFormat === 'JPG' || targetFormat === 'WEBP') && (
            <div style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={labelStyle}>Compression Resolution Quality</span>
                <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--secondary)', background: 'rgba(14, 165, 233, 0.06)', padding: '2px 8px', borderRadius: '6px' }}>
                  {quality}%
                </span>
              </div>
              <input 
                type="range"
                min="10"
                max="100"
                value={quality}
                onChange={(e) => setQuality(parseInt(e.target.value))}
                style={{ width: '100%', accentColor: 'var(--secondary)', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                High quality (80%-90%) maintains optimal file sharpness. Lower qualities yield smaller sizes.
              </span>
            </div>
          )}

          {/* Error display */}
          {errorMsg && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.15)',
              padding: '14px 20px',
              borderRadius: '12px',
              color: '#ef4444',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '0.85rem',
              fontWeight: 600
            }}>
              <AlertCircle size={16} /> {errorMsg}
            </div>
          )}

          {/* Trigger Dispatch Conversion Actions */}
          <button
            onClick={performConversion}
            disabled={isConverting}
            style={{
              height: '48px',
              border: 'none',
              background: 'linear-gradient(90deg, var(--secondary) 0%, var(--primary) 100%)',
              color: '#ffffff',
              borderRadius: '12px',
              fontSize: '0.95rem',
              fontWeight: 700,
              cursor: isConverting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'var(--transition-smooth)'
            }}
            className="action-convert-btn"
          >
            {isConverting ? <RefreshCw size={16} className="spinner" /> : <Settings size={16} />}
            {isConverting ? 'Processing Conversion...' : `Convert from ${sourceFormat} to ${targetFormat}`}
          </button>

        </div>

        {/* Right column: Previews and download controls */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }} className="converter-preview-column">
          
          {/* Active original preview */}
          {filePreview && (
            <div style={cardStyle}>
              <span style={labelStyle}>Original Image Preview</span>
              <div style={{
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid var(--border-color)',
                aspectRatio: '16/10',
                background: 'var(--bg-secondary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <img 
                  src={filePreview} 
                  alt="Original Asset" 
                  style={{ maxWith: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              </div>
            </div>
          )}

          {/* Converted Outputs panel card */}
          {convertedResult && (
            <div style={{
              background: '#ffffff',
              border: '1px solid var(--border-color)',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: 'var(--shadow-md)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <span style={{ ...labelStyle, color: '#059669' }}>Converted Success Outputs</span>
                <span style={{ fontSize: '0.72rem', background: 'rgba(5, 150, 105, 0.08)', color: '#059669', padding: '2px 8px', borderRadius: '4px', fontWeight: 800 }}>
                  Ready
                </span>
              </div>

              {/* Dynamic layouts previews based on formats */}
              {targetFormat === 'Base64' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <textarea
                    readOnly
                    value={convertedResult}
                    style={{
                      width: '100%',
                      height: '140px',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      borderRadius: '8px',
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-color)',
                      padding: '10px',
                      outline: 'none',
                      resize: 'none'
                    }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    <span>Estimated Length: <strong>{convertedSize}</strong></span>
                    <button
                      onClick={copyBase64}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: 'var(--secondary)',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      {isCopied ? <Check size={12} style={{ color: '#059669' }} /> : <Copy size={12} />}
                      {isCopied ? 'Copied' : 'Copy Code'}
                    </button>
                  </div>
                </div>
              ) : targetFormat === 'PDF' ? (
                <div style={{
                  padding: '20px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  <FileText size={32} style={{ color: '#ef4444', margin: '0 auto' }} />
                  <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--text-primary)' }}>Your PDF print sheet has launched</span>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                    Use the native system print popup to save the document layout directly as a PDF format sheet on your device.
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div style={{
                    borderRadius: '12px',
                    overflow: 'hidden',
                    border: '1px solid var(--border-color)',
                    aspectRatio: '16/10',
                    background: 'var(--bg-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <img 
                      src={convertedResult} 
                      alt="Converted Success" 
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    <span>Calculated Final Size: <strong style={{ color: '#059669' }}>{convertedSize}</strong></span>
                    <span>Format: <strong>{targetFormat}</strong></span>
                  </div>

                  <AdToast message={toast} />
                  <button
                    onClick={withAd('format-convert', handleDownload)}
                    style={{
                      height: '42px',
                      border: 'none',
                      background: '#059669',
                      color: '#ffffff',
                      borderRadius: '10px',
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      transition: 'var(--transition-smooth)'
                    }}
                    className="download-converted-btn"
                  >
                    <Download size={14} /> Download {targetFormat} File
                  </button>
                </div>
              )}
            </div>
          )}

        </div>

      </div>

      {/* Styled overridden transitions tags */}
      <style>{`
        @media (min-width: 992px) {
          .converter-workspace-grid {
            grid-template-columns: 1fr 420px !important;
          }
        }

        .upload-dropzone:hover {
          border-color: var(--secondary) !important;
          background: rgba(14, 165, 233, 0.01) !important;
        }

        .action-convert-btn:hover {
          opacity: 0.95;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(14, 165, 233, 0.15) !important;
        }

        .download-converted-btn:hover {
          background: #047857 !important;
          transform: translateY(-1px);
        }

        .spinner {
          animation: spin 1.2s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

    {/* SEO: FAQ & Related Tools */}
      <AdUnit />
      <ToolPageExtras toolId="format-converter" />
    </div>
  );
};

// Style configurations
const cardStyle = {
  background: '#ffffff',
  border: '1px solid var(--border-color)',
  borderRadius: '20px',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  boxShadow: 'var(--shadow-sm)'
};

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: 800,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

const selectStyle = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: '10px',
  border: '1px solid var(--border-color)',
  fontSize: '0.95rem',
  fontWeight: 600,
  outline: 'none',
  background: 'var(--bg-secondary)',
  color: 'var(--text-primary)',
  boxSizing: 'border-box',
  cursor: 'pointer'
};

export default FormatConverter;
